/**
 * Assignment Utilities
 * Reusable functions for assignment status, filtering, and point management
 */

import { extractDateInTimezoneSync, parseDueDate } from './timezoneUtils'

// Assignment status constants
export const ASSIGNMENT_STATUS = {
  PENDING: 'pending',
  ASSIGNED: 'assigned',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  VERIFIED: 'verified'
} as const

export type AssignmentStatus = typeof ASSIGNMENT_STATUS[keyof typeof ASSIGNMENT_STATUS]

// Status display labels
export const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending (Not started)',
  assigned: 'Assigned',
  in_progress: 'In Progress',
  completed: 'Completed (Awaiting verification)',
  verified: 'Verified'
}

// Status colors for UI
export const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  pending: { bg: 'bg-gray-100', text: 'text-gray-800' },
  assigned: { bg: 'bg-blue-100', text: 'text-blue-800' },
  in_progress: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
  completed: { bg: 'bg-orange-100', text: 'text-orange-800' },
  verified: { bg: 'bg-green-100', text: 'text-green-800' }
}

interface Assignment {
  id: string | number
  status: string
  assigned_to?: string
  assigned_user?: { id?: string; display_name?: string }
  user_id?: number
  verified_at?: string
  completed_at?: string
  due_date?: string
  points_offered?: number
  points_earned?: number
}

interface FilterOptions {
  todayDateString?: string
}

/**
 * Get assigned user ID with fallback support for different data formats
 * Handles legacy user_id, new assigned_to, and nested assigned_user.id
 */
export function getAssignedUserId(assignment: Assignment): string {
  return assignment.assigned_to ||
         assignment.assigned_user?.id ||
         (assignment.user_id ? String(assignment.user_id) : '')
}

/**
 * Check if assignment is in history (completed/verified state)
 */
export function isHistoricalAssignment(assignment: Assignment): boolean {
  return assignment.status === ASSIGNMENT_STATUS.VERIFIED || !!assignment.verified_at
}

/**
 * Check if assignment needs verification
 *
 * completed_at is authoritative for "kid did their part" — status can lag
 * (e.g. if the rejection/revert path didn't clear completed_at, or if a
 * historical row has status=pending but completed_at set from a prior
 * completion). Any row where the kid has marked done and a parent hasn't
 * verified yet needs verification.
 */
export function needsVerification(assignment: Assignment): boolean {
  const doneByKid =
    assignment.status === ASSIGNMENT_STATUS.COMPLETED ||
    !!assignment.completed_at
  const notYetVerified =
    assignment.status !== ASSIGNMENT_STATUS.VERIFIED && !assignment.verified_at
  return doneByKid && notYetVerified
}

/**
 * Compute the display status for the ChoreStatusBadge / stepper.
 * Prefers verification state, then kid-completion state, then raw status.
 * This is what parent-facing UI should show so its badge agrees with the
 * mobile kid view and the underlying data across all drift scenarios.
 */
export function getDisplayStatus(assignment: Assignment): AssignmentStatus | string {
  if (assignment.verified_at || assignment.status === ASSIGNMENT_STATUS.VERIFIED) {
    return ASSIGNMENT_STATUS.VERIFIED
  }
  if (assignment.completed_at || assignment.status === ASSIGNMENT_STATUS.COMPLETED) {
    return ASSIGNMENT_STATUS.COMPLETED
  }
  return assignment.status
}

/**
 * Check if assignment is overdue
 */
export function isOverdue(assignment: Assignment, todayDateString: string): boolean {
  if (!todayDateString || !assignment.due_date) return false
  if (assignment.status === ASSIGNMENT_STATUS.COMPLETED ||
      assignment.status === ASSIGNMENT_STATUS.VERIFIED ||
      assignment.verified_at) {
    return false
  }
  const dueDateStr = extractDateInTimezoneSync(assignment.due_date)
  return dueDateStr < todayDateString
}

/**
 * Check if assignment is active (not completed/verified and not overdue)
 */
export function isActive(assignment: Assignment, todayDateString: string): boolean {
  if (assignment.status === ASSIGNMENT_STATUS.COMPLETED ||
      assignment.status === ASSIGNMENT_STATUS.VERIFIED ||
      assignment.verified_at) {
    return false
  }
  if (todayDateString && isOverdue(assignment, todayDateString)) {
    return false
  }
  return [ASSIGNMENT_STATUS.PENDING, ASSIGNMENT_STATUS.ASSIGNED, ASSIGNMENT_STATUS.IN_PROGRESS]
    .includes(assignment.status as AssignmentStatus)
}

/**
 * Filter assignments into categories
 */
export function filterAssignments(
  assignments: Assignment[],
  options: FilterOptions = {}
): {
  all: Assignment[]
  needsVerification: Assignment[]
  overdue: Assignment[]
  active: Assignment[]
  history: Assignment[]
} {
  const { todayDateString = '' } = options

  return {
    all: assignments,
    needsVerification: assignments.filter(a => needsVerification(a)),
    overdue: assignments.filter(a => isOverdue(a, todayDateString)),
    active: assignments.filter(a => isActive(a, todayDateString)),
    history: assignments.filter(a => isHistoricalAssignment(a))
  }
}

/**
 * Sort assignments by a given field
 */
export function sortAssignments(
  assignments: Assignment[],
  sortBy: 'date' | 'status' | 'child' = 'date',
  sortOrder: 'asc' | 'desc' = 'desc',
  getChildName?: (assignment: Assignment) => string
): Assignment[] {
  return [...assignments].sort((a, b) => {
    let compareValue = 0

    switch (sortBy) {
      case 'date':
        const dateA = parseDueDate(a.due_date || a.completed_at || '').getTime()
        const dateB = parseDueDate(b.due_date || b.completed_at || '').getTime()
        compareValue = dateB - dateA
        break
      case 'status':
        compareValue = a.status.localeCompare(b.status)
        break
      case 'child':
        if (getChildName) {
          compareValue = getChildName(a).localeCompare(getChildName(b))
        }
        break
    }

    return sortOrder === 'desc' ? compareValue : -compareValue
  })
}

/**
 * Determine if points should be awarded immediately on completion
 * Based on family-level chore_verification feature setting
 */
export function shouldAwardPointsOnComplete(isVerificationEnabled: boolean): boolean {
  return !isVerificationEnabled
}

/**
 * Get points to display for an assignment
 */
export function getDisplayPoints(assignment: Assignment): number {
  return assignment.points_earned || assignment.points_offered || 0
}

/**
 * Check if a status transition involves point changes
 * Returns: 'add' | 'remove' | 'none'
 */
export function getPointChangeType(
  oldStatus: string,
  newStatus: string
): 'add' | 'remove' | 'none' {
  const wasVerified = oldStatus === ASSIGNMENT_STATUS.VERIFIED
  const willBeVerified = newStatus === ASSIGNMENT_STATUS.VERIFIED

  if (!wasVerified && willBeVerified) {
    return 'add'
  }
  if (wasVerified && !willBeVerified) {
    return 'remove'
  }
  return 'none'
}
